import React, { useState, useEffect, useCallback } from 'react';
import { Switch, Route, NavLink, useParams, useRouteMatch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import { CSSTransition } from 'react-transition-group';
import { useTranslation } from 'react-i18next';
import eventStartEndFormat from '../../utils/eventStartEndFormat';
import { eventsFetch, eventFetch, partnerPortalSettingsFetch, fetchCart } from '../../actions';
import moment from 'moment';

import PageHeader from '../Layout/PageHeader/PageHeader';
import EventOverview from './EventOverview/EventOverview';
import EventPersonsContainer from './EventPersons/EventPersonsContainer';
import PartnerContainer from '../Partner/PartnerContainer';
import ScanPoint from '../ScanPoints/ScanPoint/ScanPoint';
import ScanPointsContainer from '../ScanPoints/ScanPointsContainer';
import ScanPointOrder from '../ScanPoints/ScanPointOrder/ScanPointOrder';
import ShopContainer from '../Shop/ShopContainer';
import MiniCart from '../Shop/Cart/MiniCart/MiniCart';
import Checkout from '../Shop/Checkout/Checkout';
import OrdersContainer from '../Shop/Orders/OrdersContainer';
import OrderSummary from '../Shop/Orders/OrderSummary/OrderSummary';
import NotificationsArchive from '../Notifications/NotificationsArchive/NotificationsArchive';
import EmailsContainer from '../Emails/EmailsContainer';
import Email from '../Emails/Email/Email';
import AnalyticsContainer from '../Analytics/AnalyticsContainer';

function Event() {
    const { eventID } = useParams();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const event = useSelector(state => state.event);
    const partnerPortalSettings = useSelector(state => state.partnerPortalSettings);
    const { isScanPointSalesEnabled, isProductsEnabled } = partnerPortalSettings.partnerPortalSettings;
    const { eventsById, eventsByPartnerID, isProspectumLiveEnabled } = event;
    const { userID } = useSelector(state => state.user);
    const partnerID = localStorage.getItem("partnerID");
    const [ initialized, setInitialized ] = useState(false);
    const [ cartOpen, setCartOpen] = useState(false);
    const [portalClosed, setPortalClosed] = useState(false);

    const ordersAndProductsDisabledEvents = ['8973cebe-8916-11ea-a21a-ee6a04371b85']; // List of events that have orders and products disabled.

    /**
     * Check if portal is closed and set info text / closed prop passed to products / cart for disabling ordering
     */
    useEffect(() => {
        const settings = partnerPortalSettings.partnerPortalSettings;

        if (settings && settings.eventID === eventID) {
            if (settings.timePartnerPortalCloses && settings.timePartnerPortalOpens) {
                const isOpen = moment().isBetween(moment(settings.timePartnerPortalOpens), moment(settings.timePartnerPortalCloses));

                if (!isOpen) {
                    setPortalClosed(true);
                }
            }
        }
    }, [eventID, partnerPortalSettings]);


    /**
     * Set custom css to head, remove previous
     */
    const _setCustomCss = useCallback((customCss) => {
        const styleID = 'partner-portal--custom-css';
        let style;
        const head = document.head || document.getElementsByTagName('head')[0];
        const existingStyle = document.getElementById(styleID);

        if (existingStyle) {
            head.removeChild(existingStyle);
        }

        style = document.createElement('style');
        head.appendChild(style);
        style.id = styleID;
        style.appendChild(document.createTextNode(customCss));
    }, []);


    /**
     * Set custom css if found from partner portal settings
     */
    useEffect(() => {
        if (eventID && partnerPortalSettings) {
            const settings = partnerPortalSettings.partnerPortalSettings;

            if (settings.customCss) {
                _setCustomCss(settings.customCss);
            } else {
                _setCustomCss('');
            }
        }
    }, [_setCustomCss, event.name, eventID, partnerPortalSettings]);

    
    let { url } = useRouteMatch();

    const handleCartOpen = (cartOpen) => {
        setCartOpen(cartOpen)
    };

    const overViewActive = (match, location) => {
        const notificationsArchive = location.pathname.includes('/notifications');
        if (!match.isExact && !notificationsArchive) return false;
        return true;
    };

    const PageHeaderNav = (
        <ul className="nav">
            <li className="nav-item active event--nav--summary">
                <NavLink
                    to={`${url}`}
                    activeClassName='active'
                    isActive={overViewActive}
                >
                    {t('events.summary')}
                </NavLink>
            </li>
            <li className="nav-item event--nav--partner-info">
                <NavLink to={`${url}/exhibitor`} activeClassName='active'>{t('events.organization_info')}</NavLink>
            </li>
            <li className="nav-item event--nav--people">
                <NavLink to={`${url}/people`} activeClassName='active'>{t('events.people')}</NavLink>
            </li>
            <li className="nav-item event--nav--scanpoints">
                <NavLink to={`${url}/scanpoints`} activeClassName='active'>{t('events.participant_scanning')}</NavLink>
            </li>

            {!ordersAndProductsDisabledEvents.includes(eventID) ?
                <>
                    {isProductsEnabled ?
                        <li className="nav-item event--nav--shop">
                            <NavLink to={`${url}/shop`} activeClassName='active'>{t('events.products')}</NavLink>
                        </li>
                     : null}
                    
                    <li className="nav-item event--nav--orders">
                        <NavLink to={`${url}/orders`} activeClassName='active'>{t('events.orders')}</NavLink>
                    </li>
                </>
            : null}
           
            <li className="nav-item event--nav--emails">
                <NavLink to={`${url}/emails`} activeClassName='active'>{t('events.invitations')}</NavLink>
            </li>
            {isProspectumLiveEnabled ?
                <li className="nav-item event--nav--analytics">
                    <NavLink to={`${url}/analytics`} activeClassName='active'>{t('events.analytics')}</NavLink>
                </li>
            : null}
            {!ordersAndProductsDisabledEvents.includes(eventID) && isProductsEnabled ?
                <li className="nav-item event--nav--cart" style={{float:'right'}}>
                    <MiniCart url={url} handleCartOpen={handleCartOpen}></MiniCart>
                </li>
            : null}
        </ul>
    );

    /**
     * Fetch events if they haven't been already fetched.
     */
    useEffect(() => {
        dispatch(fetchCart(eventID))
    }, [dispatch, eventID]);

    /**
     * Fetch events if they haven't been already fetched.
     */
    useEffect(() => {
        if ((!event.events || !event.events.length) && userID) {
            dispatch(eventsFetch(userID))
        }
    }, [dispatch, userID, event.events]);

    /**
     * Fetch partner portal settings
     */
    useEffect(() => {
        dispatch(partnerPortalSettingsFetch(eventID));
    }, [dispatch, eventID]);

    /**
     * Fetch event.
     */
    useEffect(() => {
        dispatch(eventFetch(eventID, partnerID));
    }, [eventID, partnerID, eventsById, eventsByPartnerID, dispatch])


    /**
     * Set initialized after event and events are loaded.
     */
    useEffect(() => {
        if(!event.loading && !event.eventsLoading) {
            setInitialized(true)
        }
    }, [event]);

    return (
        <>
            <CSSTransition
                in={initialized}
                timeout={800}
                classNames="FadeIn"
                unmountOnExit
            >
                {(event.events.length && !event.loading)
                ?
                    <React.Fragment>
                        <div style={{minHeight: '100px'}}>
                            <PageHeader
                                title={event.name}
                                nav={PageHeaderNav}
                                cartOpen={cartOpen}
                                handleCartOpen={handleCartOpen}
                                subtitle={eventStartEndFormat(event) + (event.venue && event.venue.name ?  ', '  + event.venue.name : '')}
                            >
                            </PageHeader>
                        </div>
                        <div className="container">
                            <Switch>
                                <Route exact path={`${url}`}>
                                    <EventOverview
                                        event={event}
                                        loading={event.loading}
                                    />
                                </Route>
                                <Route path={`${url}/exhibitor`}>
                                    <PartnerContainer
                                        url={`${url}/exhibitor`}
                                        eventID={eventID}
                                        partnerID={partnerID}
                                        organizationID={event.organizationID}
                                        isProspectumLiveEnabled={isProspectumLiveEnabled}
                                        partnerPortalSettings={partnerPortalSettings.partnerPortalSettings}
                                    />
                                </Route>
                                <Route exact path={`${url}/scanpoints`}>
                                    <ScanPointsContainer eventID={eventID} />
                                </Route>
                                {isScanPointSalesEnabled ?
                                    <Route path={`${url}/scanpoints/order`}>
                                        <ScanPointOrder
                                            eventID={eventID}
                                            partnerID={partnerID}
                                            organizationID={event.organizationID}
                                        />
                                    </Route>
                                : null}
                                <Route path={`${url}/scanpoints/:scanPointID`}>
                                    <ScanPoint
                                        eventID={eventID}
                                        event={event}
                                        partnerID={partnerID}
                                        organizationID={event.organizationID}
                                    />
                                </Route>
                                <Route path={`${url}/people`}>
                                    <EventPersonsContainer 
                                        eventID={eventID}
                                        url={`${url}/people`}
                                    />
                                </Route>
                                {isProductsEnabled ?
                                    <Route exact path={`${url}/shop`}>
                                        <ShopContainer
                                            eventID={eventID}
                                            portalClosed={portalClosed}
                                        />
                                    </Route> 
                                : null}
                                
                                 {isProductsEnabled ?
                                    <Route exact path={`${url}/checkout`}>
                                        <Checkout
                                            eventID={eventID}
                                            portalClosed={portalClosed}
                                        />
                                    </Route>
                                : null}
                                <Route path={`${url}/orders`}>
                                    <OrdersContainer
                                        eventID={eventID}
                                        url={`${url}/orders`}
                                    />
                                </Route>
                                <Route exact path={`${url}/emails`}>
                                    <EmailsContainer
                                        eventID={eventID}
                                        limit={25}
                                    />
                                </Route>
                                <Route exact path={`${url}/emails/:partnerEmailId`}>
                                    <Email
                                        eventID={eventID}
                                        limit={25}
                                    />
                                </Route>
                                <Route exact path={`${url}/orderSummary/:orderID`}>
                                    <OrderSummary eventID={eventID} />
                                </Route>
                                <Route path={`${url}/notifications`}>
                                    <NotificationsArchive eventID={eventID} />
                                </Route>
                                <Route path={`${url}/analytics`}>
                                    <AnalyticsContainer
                                        eventID={eventID}
                                        url={`${url}/analytics`}
                                    />
                                </Route>
                            </Switch>
                        </div>
                    </React.Fragment>
                :
                <div style={{textAlign: 'center', marginTop: '40px'}}>
                    <Spinner animation="border" role="status">
                        <span className="sr-only">{t('general.loading')}</span>
                    </Spinner>
                    <p style={{marginTop: '10px'}}>{t('events.loading_event')}</p>
                </div>
                }
            </CSSTransition>
        </>
    )
}

export default Event;